import type { UIMessage } from 'ai';
import {
  getBagelToolName,
  getBagelToolOutput,
  isBagelToolInProgress,
  isBagelToolPart,
  type BagelChatMessagePart,
} from './bagelToolParts';

function isTextPart(
  part: BagelChatMessagePart,
): part is Extract<BagelChatMessagePart, { type: 'text' }> {
  return part.type === 'text' && Boolean(part.text?.trim());
}

/**
 * When the model uses tools, streamText often emits interim preamble text
 * ("Let me search…") in an early step and the real answer in a later step.
 * Joining or showing all text parts glues them together (e.g. "...moment.I").
 */
export function getAssistantDisplayParts(
  parts: BagelChatMessagePart[] | undefined,
  opts: { isStreaming: boolean; suppressInterimText?: boolean },
): BagelChatMessagePart[] | undefined {
  if (!parts?.length) return parts;

  const hasTool = parts.some(part => isBagelToolPart(part));
  if (!hasTool) {
    if (opts.isStreaming && opts.suppressInterimText) {
      return parts.filter(part => part.type !== 'text');
    }
    return parts;
  }

  const toolParts = parts.filter(part => isBagelToolPart(part));

  const lastToolIndex = parts.findLastIndex(part => isBagelToolPart(part));
  const postToolTextParts = parts.filter(
    (part, index) => isTextPart(part) && index > lastToolIndex,
  );

  if (postToolTextParts.length === 0) {
    return toolParts;
  }

  return [...toolParts, postToolTextParts[postToolTextParts.length - 1]];
}

export function getAssistantDisplayText(
  message: {
    role: string;
    content?: string;
    parts?: BagelChatMessagePart[] | UIMessage['parts'];
  },
  opts: { isStreaming?: boolean; suppressInterimText?: boolean } = {},
): string {
  if (message.role !== 'assistant') {
    if (message.parts?.length) {
      return message.parts
        .filter(
          (
            part,
          ): part is Extract<BagelChatMessagePart, { type: 'text' }> =>
            part.type === 'text',
        )
        .map(part => part.text ?? '')
        .join('');
    }
    return message.content ?? '';
  }

  const displayParts = getAssistantDisplayParts(
    message.parts as BagelChatMessagePart[] | undefined,
    {
      isStreaming: opts.isStreaming ?? false,
      suppressInterimText: opts.suppressInterimText,
    },
  );

  if (displayParts?.length) {
    return displayParts
      .filter(
        (part): part is Extract<BagelChatMessagePart, { type: 'text' }> =>
          isTextPart(part),
      )
      .map(part => part.text)
      .join('\n\n');
  }

  return message.content ?? '';
}

/** Last non-empty step text — avoids persisting tool-call preamble. */
export function getFinalAssistantTextFromSteps(
  steps: Array<{ text?: string }>,
  fallback = '',
): string {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const trimmed = steps[i]?.text?.trim();
    if (trimmed) return steps[i].text!;
  }
  return fallback;
}

export { getBagelToolName, getBagelToolOutput, isBagelToolInProgress, isBagelToolPart };
